import { z } from 'zod';
import { QualityLevelSchema } from '../pricing/index.js';
export { QualityLevelSchema };
export const PresetIdSchema = z.enum([
    'realistic',
    'flat_illustration',
    'pixel_art',
    'isometric',
    'logo',
    'custom',
]);
/** @deprecated Use QualityLevelSchema instead. Kept temporarily for gradual migration of other packages. */
export const QualityHintSchema = QualityLevelSchema;
export const FixedSizeSchema = z.enum(['1024x1024', '1024x1536', '1536x1024']);
export const CustomSizeSchema = z
    .object({
    width: z.number().int().min(512).max(3840).multipleOf(16),
    height: z.number().int().min(512).max(3840).multipleOf(16),
})
    .refine(({ width, height }) => {
    const area = width * height;
    if (area < 655_360 || area > 8_294_400)
        return false;
    const ratio = Math.max(width / height, height / width);
    return ratio <= 3;
}, { message: 'Size violates gpt-image-2 constraints (655k–8.3M pixels, ≤3:1)' });
export const ImageSizeSchema = z.union([FixedSizeSchema, CustomSizeSchema]);
export const OutputFormatSchema = z.enum(['png', 'jpeg', 'webp']);
export const BackgroundSchema = z.enum(['opaque', 'auto']);
export const PipelineTypeSchema = z.enum(['single', 'two_model']);
export const ImageDeliveryStatusSchema = z.enum(['cdn', 'inline_only']);
export const BrandContextSchema = z.strictObject({
    style_descriptor: z.string().trim().min(1).max(600).optional(),
    color_hints: z.array(z.string().trim().min(1).max(80)).max(8).optional(),
    mood_tags: z.array(z.string().trim().min(1).max(40)).max(10).optional(),
});
const GenerateInputShape = {
    preset: PresetIdSchema,
    subject: z.string().trim().min(1).max(2000),
    quality_hint: QualityLevelSchema.default('medium'),
    style_context: BrandContextSchema.optional(),
    reference_ids: z.array(z.string().uuid()).max(10).default([]),
    size: ImageSizeSchema.default('1024x1024'),
    output_format: OutputFormatSchema.default('png'),
    background: BackgroundSchema.default('auto'),
};
const GenerateInputObjectSchema = z.strictObject(GenerateInputShape);
export const GenerateRequestSchema = GenerateInputObjectSchema;
export const GenerateResponseSchema = z.strictObject({
    image_url: z.string().url().nullable(),
    image_base64: z.string().trim().min(1),
    image_handle: z.string().trim().min(1).nullable(),
    delivery_status: ImageDeliveryStatusSchema,
    expires_at: z.string().datetime().nullable(),
    preset: PresetIdSchema,
    quality_hint: QualityLevelSchema,
    pipeline: PipelineTypeSchema,
    token_cost: z.number().int().nonnegative(),
    output_format: OutputFormatSchema,
    background: BackgroundSchema,
});
export const CompilerInputSchema = GenerateInputObjectSchema;
export const ModelProviderSchema = z.enum(['openai', 'openrouter']);
export const ProviderApiFormatSchema = z.enum([
    'images_generations',
    'images_edits',
    'chat_completions',
]);
export const OpenAiQualitySchema = z.enum(['low', 'medium', 'high']);
export const OpenRouterImageSizeSchema = z.enum(['1K', '2K', '4K']);
export const InputFidelitySchema = z.enum(['low', 'high']);
export const ModelParamsSchema = z.strictObject({
    quality: OpenAiQualitySchema.optional(),
    size: ImageSizeSchema,
    output_format: OutputFormatSchema,
    background: BackgroundSchema.optional(),
    input_fidelity: InputFidelitySchema.optional(),
    openrouter_image_size: OpenRouterImageSizeSchema.optional(),
});
export const ModelConfigSchema = z.strictObject({
    provider: ModelProviderSchema,
    model_id: z.string().trim().min(1),
    api_format: ProviderApiFormatSchema,
    params: ModelParamsSchema,
});
export const CompilerOutputSchema = z.strictObject({
    compiled_prompt: z.string().trim().min(1),
    stage1_prompt: z.string().trim().min(1).optional(),
    stage2_prompt: z.string().trim().min(1).optional(),
    model_config: ModelConfigSchema,
    refinement_config: ModelConfigSchema.optional(),
    fallback_config: ModelConfigSchema.optional(),
    pipeline: PipelineTypeSchema,
    token_cost: z.number().int().nonnegative(),
    word_count: z.number().int().nonnegative(),
    segments_included: z.array(z.string().trim().min(1)),
    segments_excluded: z.array(z.string().trim().min(1)),
});
//# sourceMappingURL=generation.js.map